<template>
  <div class="coi-import-page">
    <!-- Breadcrumb Navigation -->
    <div class="breadcrumb">
      <router-link to="/" class="breadcrumb-item">
        <i class="fas fa-home"></i>
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-item">Data Import & Onboarding</span>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-item active">Import from COI</span>
    </div>

    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-icon">
          <i class="fas fa-file-import"></i>
        </div>
        <div class="header-text">
          <h1 class="page-title">Import from Certificate of Insurance</h1>
          <p class="page-subtitle">
            Create compliance subjects by analyzing Certificate of Insurance documents. 
            Upload new COI documents or select from existing documents in your projects.
          </p>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="import-content">
      
      <!-- Upload New COI Section -->
      <div class="import-section upload-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="fas fa-cloud-upload-alt"></i>
          </div>
          <div class="section-content">
            <h3>Upload New COI Document</h3>
            <p>Drag and drop a Certificate of Insurance document to begin analysis</p>
          </div>
        </div>

        <!-- Project Selector for Upload -->
        <div class="project-selector upload-project-selector">
          <label for="upload-project-select">Select Project for Upload:</label>
          <select 
            id="upload-project-select" 
            v-model="uploadProjectId" 
            class="form-control"
          >
            <option value="">Choose a project...</option>
            <option v-for="project in projects" :key="project.id" :value="project.id">
              {{ project.name }}
            </option>
          </select>
        </div>

        <div class="file-upload-area" 
             :class="{ 'dragging': isDragging, 'disabled': uploading }"
             @drop="handleDrop"
             @dragover.prevent
             @dragenter.prevent="isDragging = true"
             @dragleave.prevent="isDragging = false">
          <input 
            type="file" 
            ref="fileInput" 
            @change="handleFileSelect"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            style="display: none"
            :disabled="uploading"
          />
          
          <div class="upload-content">
            <i class="fas fa-cloud-upload-alt upload-icon"></i>
            <h4 v-if="selectedFile">{{ selectedFile.name }}</h4>
            <h4 v-else>Drop COI document here or click to browse</h4>
            <p v-if="!selectedFile">Supported formats: PDF, DOC, DOCX, PNG, JPG</p>
            <button 
              v-if="!selectedFile"
              @click="$refs.fileInput.click()" 
              type="button" 
              class="btn btn-primary upload-btn"
              :disabled="uploading"
            >
              <i class="fas fa-file-plus"></i>
              Choose File
            </button>
            <div v-if="selectedFile" class="file-actions">
              <button 
                @click="processUploadedFile" 
                class="btn btn-success"
                :disabled="!canUploadFile"
                :title="!uploadProjectId ? 'Please select a project first' : ''"
              >
                <i v-if="uploading" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-magic"></i>
                {{ uploading ? 'Processing...' : 'Analyze COI' }}
              </button>
              <button 
                @click="clearSelection" 
                class="btn btn-outline-secondary"
                :disabled="uploading"
              >
                <i class="fas fa-times"></i>
                Clear
              </button>
            </div>
            <div v-if="selectedFile && !uploadProjectId" class="upload-warning">
              <i class="fas fa-exclamation-triangle"></i>
              Please select a project above to continue with the upload
            </div>
          </div>
        </div>
      </div>

      <!-- Divider -->
      <div class="section-divider">
        <span>OR</span>
      </div>

      <!-- Select from Existing Documents -->
      <div class="import-section existing-documents-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="fas fa-folder-open"></i>
          </div>
          <div class="section-content">
            <h3>Select from Existing Documents</h3>
            <p>Choose a COI document from your existing project documents</p>
          </div>
        </div>

        <!-- Project Selector -->
        <div class="project-selector">
          <label for="project-select">Select Project:</label>
          <select 
            id="project-select" 
            v-model="selectedProjectId" 
            @change="loadProjectDocuments"
            class="form-control"
          >
            <option value="">Choose a project...</option>
            <option v-for="project in projects" :key="project.id" :value="project.id">
              {{ project.name }}
            </option>
          </select>
        </div>

        <!-- Documents Grid -->
        <div v-if="loadingDocuments" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          Loading documents...
        </div>

        <div v-else-if="filteredDocuments.length === 0 && selectedProjectId" class="empty-state">
          <i class="fas fa-file-alt"></i>
          <p>No COI documents found in this project.</p>
        </div>

        <div v-else-if="filteredDocuments.length > 0" class="documents-grid">
          <div 
            v-for="document in filteredDocuments" 
            :key="document.id" 
            class="document-card"
            :class="{ 'selected': selectedDocument?.id === document.id }"
            @click="selectDocument(document)"
          >
            <div class="document-icon">
              <i class="fas fa-file-pdf"></i>
            </div>
            <div class="document-info">
              <h5>{{ document.name }}</h5>
              <p class="document-type">{{ getDocumentTypeLabel(document.document_type) }}</p>
              <small class="upload-date">{{ formatDate(document.created_at) }}</small>
            </div>
            <div class="document-actions">
              <button 
                @click.stop="processExistingDocument(document)" 
                class="btn btn-sm btn-success"
                :disabled="processing"
              >
                <i v-if="processing && selectedDocument?.id === document.id" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-magic"></i>
                Analyze
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- COI Review Step -->
      <div v-if="showReviewStep" class="import-section review-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="fas fa-search"></i>
          </div>
          <div class="section-content">
            <h3>Review COI Data</h3>
            <p>Please review the extracted information and select which entity should be the compliance subject</p>
          </div>
        </div>

        <div class="review-content">
          <!-- Vertical Layout for Data Review then Document -->
          <div class="split-pane-layout vertical-layout">
            <!-- Top Panel: Entity Selection -->
            <div class="data-panel">
              <div class="panel-header">
                <h4><i class="fas fa-users"></i> Extracted Entities</h4>
                <p class="panel-subtitle">Choose which entity should be the compliance subject</p>
              </div>
              
              <div class="entity-selection">
                <div class="entity-options">
                  <label class="entity-option" v-if="parsedCoiData?.policyholder?.name">
                    <input type="radio" v-model="selectedSubjectSourceKey" value="policyholder">
                    <div class="entity-card">
                      <div class="entity-type">Policyholder</div>
                      <div class="entity-name">{{ parsedCoiData.policyholder.name }}</div>
                      <div class="entity-address">{{ parsedCoiData.policyholder.address }}</div>
                    </div>
                  </label>
                  
                  <label class="entity-option" v-if="parsedCoiData?.producer?.name">
                    <input type="radio" v-model="selectedSubjectSourceKey" value="producer">
                    <div class="entity-card">
                      <div class="entity-type">Producer</div>
                      <div class="entity-name">{{ parsedCoiData.producer.name }}</div>
                      <div class="entity-address">{{ parsedCoiData.producer.address }}</div>
                    </div>
                  </label>
                  
                  <label class="entity-option" v-if="parsedCoiData?.certificate_recipient?.name">
                    <input type="radio" v-model="selectedSubjectSourceKey" value="certificate_recipient">
                    <div class="entity-card">
                      <div class="entity-type">Certificate Recipient</div>
                      <div class="entity-name">{{ parsedCoiData.certificate_recipient.name }}</div>
                      <div class="entity-address">{{ parsedCoiData.certificate_recipient.address }}</div>
                    </div>
                  </label>
                </div>
              </div>
              <!-- Display all producer contacts to be created for this subject -->
              <div v-if="parsedCoiData.producer_contacts?.length" class="contacts-section">
                <h5>Producer Contacts</h5>
                <ul class="contact-list">
                  <li v-for="contact in parsedCoiData.producer_contacts" :key="contact.email">
                    <strong>{{ contact.name }}</strong> - <a :href="`mailto:${contact.email}`">{{ contact.email }}</a>
                  </li>
                </ul>
              </div>
              <!-- Review Actions -->
              <div class="review-actions">
                <button 
                  @click="confirmCoiCreation" 
                  class="btn btn-success"
                  :disabled="confirming"
                >
                  <i v-if="confirming" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fas fa-check"></i>
                  {{ confirming ? 'Creating Subject...' : 'Create Compliance Subject' }}
                </button>
                <button 
                  @click="showReviewStep = false" 
                  class="btn btn-outline-secondary"
                  :disabled="confirming"
                >
                  <i class="fas fa-times"></i>
                  Cancel
                </button>
              </div>
            </div>

            <!-- Bottom Panel: Document Viewer -->
            <div class="document-panel">
              <div class="panel-header">
                <h4><i class="fas fa-file-alt"></i> Source Document</h4>
                <p class="panel-subtitle">Review the original COI to validate extracted data</p>
              </div>
              <div class="document-viewer">
                <div v-if="currentCoiDocument" class="document-content">
                  <div class="document-info">
                    <h5>{{ currentCoiDocument.name }}</h5>
                    <span class="document-meta">Uploaded: {{ formatDate(currentCoiDocument.created_at) }}</span>
                  </div>
                  <div class="document-actions">
                    <button 
                      @click="viewDocument(currentCoiDocument)" 
                      class="btn btn-outline-primary btn-sm"
                    >
                      <i class="fas fa-external-link-alt"></i> View Full Size
                    </button>
                    <a 
                      :href="getDownloadUrl(currentCoiDocument)" 
                      class="btn btn-outline-secondary btn-sm"
                    >
                      <i class="fas fa-download"></i> Download
                    </a>
                  </div>
                  <!-- Document preview area -->
                  <div class="document-preview">
                    <div v-if="isPDF(currentCoiDocument)" class="pdf-container">
                      <embed 
                        :src="getDocumentUrl(currentCoiDocument)" 
                        type="application/pdf"
                        class="pdf-preview"
                        title="COI Document Preview" 
                      />
                      <div class="pdf-fallback">
                        <p><i class="fas fa-file-pdf"></i> PDF Viewer</p>
                        <p class="small">If the PDF doesn't display above, <button @click="viewDocument(currentCoiDocument)" class="link-button">open in new tab</button></p>
                      </div>
                    </div>
                    <div v-else class="preview-placeholder">
                      <i class="fas fa-file-alt"></i>
                      <p>Document preview not available for this file type</p>
                      <p class="small">Click "View Full Size" to open in new tab</p>
                    </div>
                  </div>
                </div>
                <div v-else class="no-document">
                  <i class="fas fa-file-question"></i>
                  <p>No document available for preview</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Processing Results -->
      <div v-if="analysisResult" class="results-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="fas fa-check-circle text-success"></i>
          </div>
          <div class="section-content">
            <h3>Analysis Complete</h3>
            <p>Successfully created compliance subject from COI document</p>
          </div>
        </div>

        <div class="result-card">
          <div class="result-content">
            <h4>{{ analysisResult.subject_name }}</h4>
            <p><strong>Entity:</strong> {{ analysisResult.entity_name }}</p>
            <p><strong>Project:</strong> {{ analysisResult.project_name }}</p>
            <div class="result-actions">
              <router-link 
                :to="`/projects/${analysisResult.project_id}/compliance`" 
                class="btn btn-primary"
              >
                <i class="fas fa-eye"></i>
                View in Compliance Dashboard
              </router-link>
              <button @click="resetForm" class="btn btn-outline-secondary">
                <i class="fas fa-plus"></i>
                Import Another COI
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Display -->
      <div v-if="error" class="error-section">
        <div class="alert alert-danger">
          <i class="fas fa-exclamation-triangle"></i>
          <strong>Error:</strong> {{ error }}
          <button @click="error = null" class="btn-close">×</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'CoiImport',
  data() {
    return {
      // File upload
      selectedFile: null,
      isDragging: false,
      uploading: false,
      
      // Project & documents
      projects: [],
      selectedProjectId: '', // For existing document selection
      uploadProjectId: '', // For new file upload
      uploadedDocumentInfo: null, // Store uploaded document for preview
      projectDocuments: [],
      loadingDocuments: false,
      
      // Document selection
      selectedDocument: null,
      processing: false,
      
      // COI Review step (like ComprehensiveAnalysisDashboard)
      showReviewStep: false,
      parsedCoiData: null,
      selectedSubjectSourceKey: 'policyholder',
      confirming: false,
      
      // Results
      analysisResult: null,
      error: null
    };
  },
  computed: {
    filteredDocuments() {
      // Filter for COI/Certificate documents
      return this.projectDocuments.filter(doc => 
        doc.document_type === 'CERTIFICATE' || 
        doc.document_type === 'INSURANCE_POLICY' ||
        doc.name.toLowerCase().includes('coi') ||
        doc.name.toLowerCase().includes('certificate')
      );
    },
    canUploadFile() {
      return this.selectedFile && this.uploadProjectId && !this.uploading;
    },
    // Get the current COI document for display (similar to ComprehensiveAnalysisDashboard)
    currentCoiDocument() {
      if (!this.parsedCoiData) return null;
      const id = this.parsedCoiData.document_id;
      if (!id) return null;
      // 1. Check loaded project documents
      const found = this.projectDocuments.find(doc => doc.id === id);
      if (found) return found;
      // 2. Check uploaded document info
      if (this.uploadedDocumentInfo && this.uploadedDocumentInfo.id === id) {
        return this.uploadedDocumentInfo;
      }
      // 3. Fallback synthetic document
      return {
        id,
        name: this.uploadedDocumentInfo?.name || this.selectedFile?.name || 'COI Document',
        file_url: this.uploadedDocumentInfo?.file_url || `/media/documents/${this.selectedFile?.name}`,
        created_at: this.uploadedDocumentInfo?.created_at || new Date().toISOString(),
        document_type: 'CERTIFICATE'
      };
    }
  },
  async mounted() {
    await this.loadProjects();
  },
  methods: {
    async loadProjects() {
      try {
        const response = await api.get('/projects/');
        
        // Extract the project array correctly using the same logic as ProjectsList
        let projectsData = [];
        if (response && response.data) {
          // Check for DRF pagination structure first
          if (Array.isArray(response.data.results)) {
            projectsData = response.data.results;
          } 
          // Then check if response.data is the array itself
          else if (Array.isArray(response.data)) {
            projectsData = response.data;
          } 
          // Add checks for other possible structures
          else if (Array.isArray(response.data.projects)) {
            projectsData = response.data.projects;
          }
          else {
            console.warn("Unexpected projects data structure:", response.data);
          }
        }
        
        this.projects = projectsData;
      } catch (error) {
        console.error('Error loading projects:', error);
        this.error = 'Failed to load projects';
      }
    },

    async loadProjectDocuments() {
      if (!this.selectedProjectId) {
        this.projectDocuments = [];
        return;
      }

      this.loadingDocuments = true;
      try {
        const response = await api.get(`/projects/${this.selectedProjectId}/documents/`);
        this.projectDocuments = response.data?.documents || [];
      } catch (error) {
        console.error('Error loading project documents:', error);
        this.error = 'Failed to load project documents';
      } finally {
        this.loadingDocuments = false;
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
      }
    },

    handleDrop(event) {
      event.preventDefault();
      this.isDragging = false;
      
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.selectedFile = files[0];
      }
    },

    clearSelection() {
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    selectDocument(document) {
      this.selectedDocument = document;
    },

    async processUploadedFile() {
      if (!this.selectedFile) return;
      
      if (!this.uploadProjectId) {
        this.error = 'Please select a project before uploading the COI document';
        return;
      }

      this.uploading = true;
      this.error = null;
      this.analysisResult = null;

      try {
        // Upload the file to the selected project
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        formData.append('document_type', 'CERTIFICATE');
        formData.append('name', this.selectedFile.name);

        const uploadResponse = await api.post(`/projects/${this.uploadProjectId}/documents/upload/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Now process the uploaded document for COI analysis
        // Extract the actual document from the response wrapper
        const document = uploadResponse.data.document || uploadResponse.data;
        this.uploadedDocumentInfo = document; // Store for preview
        await this.analyzeCoiDocument(document, this.uploadProjectId);
      } catch (error) {
        console.error('Error processing uploaded file:', error);
        this.error = 'Failed to process uploaded file: ' + (error.response?.data?.detail || error.message);
      } finally {
        this.uploading = false;
      }
    },

    async processExistingDocument(document) {
      this.processing = true;
      this.error = null;
      this.analysisResult = null;
      this.selectedDocument = document;

      try {
        await this.analyzeCoiDocument(document, this.selectedProjectId);
      } catch (error) {
        console.error('Error processing existing document:', error);
        this.error = 'Failed to process document: ' + (error.response?.data?.detail || error.message);
      } finally {
        this.processing = false;
      }
    },

    async analyzeCoiDocument(document, projectId) {
      // Step 1: Parse the COI (show review step, don't create subject yet)
      const parseResponse = await api.post('/subjects/parse_coi/', {
        document_id: document.id,
        project_id: Number(projectId)
      });

      if (parseResponse.data.error) {
        throw new Error(parseResponse.data.error || 'Failed to parse COI');
      }

      // Store parsed data and show review step (like ComprehensiveAnalysisDashboard)
      this.parsedCoiData = parseResponse.data;
      this.selectedSubjectSourceKey = 'policyholder'; // Default selection
      this.showReviewStep = true;
    },

    async confirmCoiCreation() {
      if (!this.parsedCoiData) return;

      this.confirming = true;
      this.error = null;

      const projectId = this.parsedCoiData.project_id || this.uploadProjectId || this.selectedProjectId;
      const documentId = this.parsedCoiData.document_id;

      try {
        const confirmResponse = await api.post('/subjects/confirm_coi_creation/', {
          document_id: documentId,
          project_id: projectId,
          policyholder: this.parsedCoiData.policyholder,
          producer: this.parsedCoiData.producer,
          certificate_recipient: this.parsedCoiData.certificate_recipient,
          insured_box_entity: this.parsedCoiData.insured_box_entity,
          producer_contacts: this.parsedCoiData.producer_contacts,
          subject_entity_source_key: this.selectedSubjectSourceKey
        });

        const createdSubject = confirmResponse.data.subject;

        this.analysisResult = {
          subject_name: createdSubject?.name || 'New Compliance Subject',
          entity_name: confirmResponse.data.entity?.name || 'Unknown Entity',
          project_id: this.uploadProjectId || this.selectedProjectId,
          project_name: this.projects.find(p => p.id == (this.uploadProjectId || this.selectedProjectId))?.name || 'Unknown Project',
          analysis_started: false,
          analysis_error: null
        };

        // Auto-run the policy-aware comprehensive check post-confirm so that
        // CoiImport behaves consistently with the ComprehensiveAnalysisDashboard
        // confirm flow. See docs/analysis/2026-05-coi-policy-filter-audit.md
        // (Phase 3).
        if (createdSubject && documentId && projectId) {
          try {
            await api.post(`/projects/${projectId}/analysis-sessions/`, {
              project: projectId,
              compliance_subject: createdSubject.id,
              name: `Analysis for ${createdSubject.subject_entity_name || createdSubject.name} (CoiImport)`,
              status: 'PROCESSING',
              documents: [{
                document_id: documentId,
                analysis_type: 'COMPREHENSIVE'
              }]
            });
            this.analysisResult.analysis_started = true;
          } catch (analysisError) {
            console.error('Error starting comprehensive analysis after COI confirm:', analysisError);
            this.analysisResult.analysis_error = analysisError.response?.data?.error || analysisError.message;
          }
        }

        this.showReviewStep = false;
      } catch (error) {
        console.error('Error confirming COI creation:', error);
        this.error = 'Failed to create compliance subject: ' + (error.response?.data?.error || error.message);
      } finally {
        this.confirming = false;
      }
    },

    resetForm() {
      this.selectedFile = null;
      this.selectedDocument = null;
      this.analysisResult = null;
      this.error = null;
      this.selectedProjectId = '';
      this.uploadProjectId = '';
      this.projectDocuments = [];
      this.showReviewStep = false;
      this.parsedCoiData = null;
      this.selectedSubjectSourceKey = 'policyholder';
      this.uploadedDocumentInfo = null; // Reset uploaded document info
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    getDocumentTypeLabel(type) {
      const labels = {
        'CERTIFICATE': 'Certificate of Insurance',
        'INSURANCE_POLICY': 'Insurance Policy',
        'CONTRACT': 'Contract',
        'OTHER': 'Other'
      };
      return labels[type] || type;
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    // Document viewing methods - use unified helper
    getDocumentUrl(doc) {
      const { getDocumentUrl } = require('@/utils/documentUrlHelper');
      return getDocumentUrl(doc);
    },

    isPDF(doc) {
      const { isPDF } = require('@/utils/documentUrlHelper');
      return isPDF(doc);
    },

    viewDocument(doc) {
      if (doc && (doc.file || doc.file_url)) {
        window.open(this.getDocumentUrl(doc), '_blank');
      }
    },
    
    getDownloadUrl(doc) {
      // Use unified download URL helper
      const { getDocumentDownloadUrl } = require('@/utils/documentUrlHelper');
      return getDocumentDownloadUrl(doc);
    }
  }
};
</script>

<style scoped>
.coi-import-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

/* Breadcrumb */
.breadcrumb {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  font-size: 0.9rem;
}

.breadcrumb-item {
  color: #6b7280;
  text-decoration: none;
  transition: color 0.2s ease;
}

.breadcrumb-item:hover {
  color: #374151;
}

.breadcrumb-item.active {
  color: #111827;
  font-weight: 500;
}

.breadcrumb-separator {
  margin: 0 0.5rem;
  color: #d1d5db;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 3rem;
  padding: 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  color: white;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.header-icon {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.page-title {
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
  font-weight: 700;
}

.page-subtitle {
  margin: 0;
  opacity: 0.9;
  font-size: 1.1rem;
  line-height: 1.5;
}

/* Import Content */
.import-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.import-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.section-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
}

.section-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.section-content p {
  margin: 0;
  color: #6b7280;
  line-height: 1.5;
}

/* File Upload Area */
.file-upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.file-upload-area:hover {
  border-color: #667eea;
  background-color: #f8faff;
}

.file-upload-area.dragging {
  border-color: #667eea;
  background-color: #f0f4ff;
  transform: scale(1.02);
}

.file-upload-area.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.upload-icon {
  font-size: 3rem;
  color: #9ca3af;
  margin-bottom: 1rem;
}

.upload-content h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.upload-content p {
  margin: 0;
  color: #6b7280;
}

.upload-btn {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
}

.file-actions {
  display: flex;
  gap: 1rem;
}

.upload-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #fef3cd;
  border: 1px solid #faebcc;
  border-radius: 6px;
  color: #856404;
  font-size: 0.9rem;
}

/* Section Divider */
.section-divider {
  position: relative;
  text-align: center;
  margin: 1rem 0;
}

.section-divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e5e7eb;
  z-index: 1;
}

.section-divider span {
  background: #f9fafb;
  padding: 0 1rem;
  color: #6b7280;
  font-weight: 500;
  z-index: 2;
  position: relative;
}

/* Project Selector */
.project-selector {
  margin-bottom: 2rem;
}

.project-selector label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Documents Grid */
.documents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
}

.document-card {
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.document-card:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.document-card.selected {
  border-color: #667eea;
  background-color: #f0f4ff;
}

.document-icon {
  width: 48px;
  height: 48px;
  background: #ef4444;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.document-info {
  flex-grow: 1;
}

.document-info h5 {
  margin: 0 0 0.25rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
}

.document-type {
  margin: 0 0 0.25rem 0;
  font-size: 0.8rem;
  color: #667eea;
  font-weight: 500;
}

.upload-date {
  color: #9ca3af;
  font-size: 0.75rem;
}

.document-actions {
  flex-shrink: 0;
}

/* States */
.loading-state, .empty-state {
  text-align: center;
  padding: 3rem;
  color: #6b7280;
}

.loading-state i, .empty-state i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #d1d5db;
}

/* Review Section */
.review-section {
  background: #fffbeb;
  border: 1px solid #f59e0b;
}

.review-content {
  margin-top: 1.5rem;
}

.entity-selection h4 {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
}

.entity-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.entity-option {
  cursor: pointer;
  display: block;
}

.entity-option input[type="radio"] {
  display: none;
}

.entity-card {
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s ease;
  background: white;
}

.entity-option input[type="radio"]:checked + .entity-card {
  border-color: #667eea;
  background-color: #f0f4ff;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.entity-card:hover {
  border-color: #667eea;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.entity-type {
  font-size: 0.8rem;
  font-weight: 600;
  color: #667eea;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.entity-name {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
}

.entity-address {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.4;
}

.contact-info {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f0f4ff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #374151;
}

.contact-info h5 {
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.contact-info p {
  margin: 0.25rem 0;
  color: #6b7280;
}

.contact-info a {
  color: #667eea;
  text-decoration: none;
}

.contact-info a:hover {
  text-decoration: underline;
}

.contacts-section {
  margin-top: 2rem;
  padding: 1rem;
  background: #f0f4ff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #374151;
}

.contacts-section h5 {
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.contact-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.contact-list li {
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
  position: relative;
}

.contact-list li::before {
  content: "•";
  color: #667eea;
  position: absolute;
  left: 0.5rem;
}

.review-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

/* Split-Pane Layout */
.split-pane-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 1.5rem;
}

.document-panel, .data-panel {
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.panel-header {
  padding: 1rem 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.panel-header h4 {
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.panel-subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
}

/* Document Viewer */
.document-viewer {
  height: 600px;
  display: flex;
  flex-direction: column;
}

.document-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1rem;
}

.document-info {
  margin-bottom: 1rem;
}

.document-info h5 {
  margin: 0 0 0.25rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
}

.document-meta {
  font-size: 0.8rem;
  color: #6b7280;
}

.document-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.document-preview {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.pdf-container {
  flex: 1;
  position: relative;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.pdf-preview {
  width: 100%;
  height: 100%;
  min-height: 400px;
  border: none;
}

.pdf-fallback {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.5rem;
  text-align: center;
  font-size: 0.8rem;
}

.preview-placeholder, .no-document {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  text-align: center;
  padding: 2rem;
}

.preview-placeholder i, .no-document i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #d1d5db;
}

.link-button {
  background: none;
  border: none;
  color: #667eea;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
}

.link-button:hover {
  color: #5a67d8;
}

/* Data Panel */
.data-panel {
  display: flex;
  flex-direction: column;
}

.data-panel .entity-selection {
  flex: 1;
  padding: 1rem;
}

.data-panel .entity-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.data-panel .review-actions {
  margin: 0 1rem 1rem 1rem;
}

/* Vertical layout override */
.split-pane-layout.vertical-layout {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  gap: 2rem;
}

/* Ensure document viewer expands in bottom panel */
.split-pane-layout.vertical-layout .document-viewer {
  height: calc(100vh - 400px);
}

/* Adjust data-panel styling if needed */
.split-pane-layout.vertical-layout .data-panel {
  /* existing data-panel styling applies */
}

/* Responsive design */
@media (max-width: 1024px) {
  .split-pane-layout {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .document-viewer {
    height: 400px;
  }
}

/* Results Section */
.results-section {
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
}

.results-section .section-icon {
  background: #10b981;
}

.result-card {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
}

.result-content h4 {
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.result-content p {
  margin: 0 0 0.5rem 0;
  color: #374151;
}

.result-actions {
  margin-top: 1.5rem;
  display: flex;
  gap: 1rem;
}

/* Error Section */
.error-section {
  margin-top: 1rem;
}

.alert {
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.alert-danger {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.btn-close {
  background: none;
  border: none;
  color: inherit;
  font-size: 1.25rem;
  cursor: pointer;
  margin-left: auto;
}

/* Buttons */
.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  text-decoration: none;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background-color: #667eea;
  color: white;
  border-color: #667eea;
}

.btn-primary:hover:not(:disabled) {
  background-color: #5a67d8;
  border-color: #5a67d8;
}

.btn-success {
  background-color: #10b981;
  color: white;
  border-color: #10b981;
}

.btn-success:hover:not(:disabled) {
  background-color: #059669;
  border-color: #059669;
}

.btn-outline-secondary {
  background-color: transparent;
  color: #6b7280;
  border-color: #d1d5db;
}

.btn-outline-secondary:hover:not(:disabled) {
  background-color: #f9fafb;
  color: #374151;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.text-success {
  color: #10b981;
}

@media (max-width: 768px) {
  .coi-import-page {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    text-align: center;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .documents-grid {
    grid-template-columns: 1fr;
  }
  
  .result-actions {
    flex-direction: column;
  }
  
  .file-actions {
    flex-direction: column;
  }
}
</style>